import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axios";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";

const SessionManagementSection = () => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // =========================
  // FETCH SESSIONS
  // =========================

  const fetchSessions = async () => {
    try {
      setLoading(true);

      const response = await axiosInstance.get("/sessions");

      setActiveSessions(response.data);

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // LOAD
  // =========================

  useEffect(() => {

    fetchSessions();

    // auto refresh every 10 sec
    const interval = setInterval(() => {
      fetchSessions();
    }, 10000);

    return () => clearInterval(interval);

  }, []);

  // =========================
  // FILTER
  // =========================

  const filteredSessions = activeSessions.filter((session) =>
    session?.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session?.ipAddress?.includes(searchTerm) ||
    session?.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // =========================
  // FORMAT DURATION
  // =========================

  const formatDuration = (date) => {

    if (!date) return "0m";

    const loginDate = new Date(date);

    const now = new Date();

    const diff = now.getTime() - loginDate.getTime();

    const hours = Math.floor(diff / (1000 * 60 * 60));

    const minutes = Math.floor(
      (diff % (1000 * 60 * 60)) / (1000 * 60)
    );

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
  };

  // =========================
  // LAST ACTIVITY
  // =========================

  const formatLastActivity = (date) => {

    if (!date) return "Unknown";

    const activityDate = new Date(date);

    const now = new Date();

    const diff = now.getTime() - activityDate.getTime();

    const minutes = Math.floor(diff / (1000 * 60));

    if (minutes < 1) return "Just now";

    if (minutes < 60) {
      return `${minutes}m ago`;
    }

    const hours = Math.floor(minutes / 60);

    return `${hours}h ago`;
  };

  // =========================
  // STATUS COLOR
  // =========================

  const getStatusColor = (status) => {

    switch (status) {

      case "active":
        return "bg-success text-success-foreground";

      case "idle":
        return "bg-warning text-warning-foreground";

      default:
        return "bg-muted text-muted-foreground";
    }
  };

  // =========================
  // DEVICE ICON
  // =========================

  const getDeviceIcon = (device = "") => {

    if (device.includes("Windows")) return "Monitor";

    if (device.includes("Mac")) return "Laptop";

    if (device.includes("Linux")) return "Terminal";

    if (device.includes("Android")) return "Smartphone";

    if (device.includes("iPhone")) return "Smartphone";

    return "Monitor";
  };

  // =========================
  // CLEAN DEVICE NAME
  // =========================

  const cleanDevice = (device = "") => {

    if (device.includes("Windows")) {
      return "Windows Device";
    }

    if (device.includes("Mac")) {
      return "Mac Device";
    }

    if (device.includes("Linux")) {
      return "Linux Device";
    }

    return device.substring(0, 50);
  };

  // =========================
  // TERMINATE ONE
  // =========================

  const handleTerminateSession = async (sessionId) => {

    try {

      await axiosInstance.delete(`/sessions/${sessionId}`);

      const currentToken = sessionStorage.getItem("token");

      const deletedSession = activeSessions.find(
        (s) => s.id === sessionId
      );

      // logout current user if own session terminated
      if (
        deletedSession &&
        deletedSession.session_id === currentToken
      ) {

        sessionStorage.clear();

        window.location.href = "/login";

        return;
      }

      fetchSessions();

    } catch (error) {

      console.error(error);
    }
  };

  // =========================
  // TERMINATE ALL
  // =========================

  const handleTerminateAllSessions = async () => {

    try {

      await axiosInstance.delete("/sessions");

      sessionStorage.clear();

      window.location.href = "/login";

    } catch (error) {

      console.error(error);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">

      {/* HEADER */}
      <div className="p-6 border-b border-border bg-muted/20">

        <div className="flex items-center justify-between mb-5">

          <div>
            <h3 className="text-xl font-semibold text-card-foreground">
              Active Sessions
            </h3>

            <p className="text-sm text-muted-foreground mt-1">
              Monitor and manage active user sessions
            </p>
          </div>

          <div className="flex items-center gap-3">

            <Button
              variant="outline"
              size="sm"
              onClick={fetchSessions}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Refresh
            </Button>

            <Button
              variant="destructive"
              size="sm"
              onClick={handleTerminateAllSessions}
              iconName="LogOut"
              iconPosition="left"
              disabled={activeSessions.length === 0}
            >
              Terminate All
            </Button>

          </div>
        </div>

        {/* STATS */}
        <div className="flex items-center justify-between">

          <div className="flex items-center gap-6">

            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-success"></div>

              <span className="text-sm text-muted-foreground">
                {
                  activeSessions.filter(
                    (s) => s.status === "active"
                  ).length
                } Active
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-warning"></div>

              <span className="text-sm text-muted-foreground">
                {
                  activeSessions.filter(
                    (s) => s.status === "idle"
                  ).length
                } Idle
              </span>
            </div>

          </div>

          <div className="w-72">
            <Input
              type="search"
              placeholder="Search sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto">

        <table className="w-full">

          <thead className="bg-muted/40">

            <tr>
              <th className="p-4 text-left">User</th>
              <th className="p-4 text-left">Device & Location</th>
              <th className="p-4 text-left">Session Info</th>
              <th className="p-4 text-left">Status</th>
              <th className="p-4 text-left">Actions</th>
            </tr>

          </thead>

          <tbody>

            {filteredSessions.map((session) => (

              <tr
                key={session.id}
                className="border-t border-border hover:bg-muted/20"
              >

                {/* USER */}
                <td className="p-4">

                  <div className="flex items-center gap-3">

                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">

                      <span className="text-white text-sm font-semibold">

                        {session?.userName
                          ?.split(" ")
                          ?.map((n) => n[0])
                          ?.join("")}

                      </span>
                    </div>

                    <div>

                      <div className="font-medium">
                        {session.userName}
                      </div>

                      <div className="text-sm text-muted-foreground">
                        {session.email}
                      </div>

                    </div>
                  </div>
                </td>

                {/* DEVICE */}
                <td className="p-4">

                  <div className="space-y-2">

                    <div className="flex items-center gap-2">

                      <Icon
                        name={getDeviceIcon(session.device)}
                        size={16}
                        className="text-muted-foreground"
                      />

                      <span className="text-sm">
                        {cleanDevice(session.device)}
                      </span>

                    </div>

                    <div className="flex items-center gap-2">

                      <Icon
                        name="MapPin"
                        size={14}
                        className="text-muted-foreground"
                      />

                      <span className="text-sm text-muted-foreground">
                        {session.location}
                      </span>

                    </div>

                    <div className="flex items-center gap-2">

                      <Icon
                        name="Globe"
                        size={14}
                        className="text-muted-foreground"
                      />

                      <span className="text-sm text-muted-foreground">
                        {session.ipAddress}
                      </span>

                    </div>

                  </div>
                </td>

                {/* SESSION */}
                <td className="p-4">

                  <div className="space-y-1">

                    <div className="text-sm">
                      Duration:{" "}
                      <span className="font-medium">
                        {formatDuration(session.loginTime)}
                      </span>
                    </div>

                    <div className="text-sm text-muted-foreground">
                      Last activity:{" "}
                      {formatLastActivity(session.lastActivity)}
                    </div>

                  </div>
                </td>

                {/* STATUS */}
                <td className="p-4">

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      session.status
                    )}`}
                  >
                    {session.status}
                  </span>

                </td>

                {/* ACTION */}
                <td className="p-4">

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      handleTerminateSession(session.id)
                    }
                    iconName="LogOut"
                    iconPosition="left"
                  >
                    Terminate
                  </Button>

                </td>

              </tr>
            ))}

          </tbody>
        </table>
      </div>

      {/* EMPTY */}
      {filteredSessions.length === 0 && (

        <div className="p-10 text-center">

          <Icon
            name="Monitor"
            size={50}
            className="mx-auto mb-4 text-muted-foreground"
          />

          <h4 className="text-lg font-semibold mb-2">
            No active sessions
          </h4>

          <p className="text-muted-foreground">
            No session records found.
          </p>

        </div>
      )}

      {/* LOADING */}
      {loading && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          Loading sessions...
        </div>
      )}

    </div>
  );
};

export default SessionManagementSection;